/**
 * Podcast Publish Scheduling Store
 *
 * Lightweight store backing the nav badge for the Podcast Publishing report. It
 * keeps a live count of episodes with a publish date that still need scheduling
 * on one or more platforms (the report's "Needs Scheduling" total) via a
 * real-time listener on episode projects.
 *
 * Kept separate from the report view so the badge works regardless of whether
 * the report is open.
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import { db } from '@/js/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { logger } from '@/services/loggerService';

const PLATFORMS = ['spotify', 'apple', 'youtube'];

/**
 * Mirror of publishSchedulingService.toDate — anything that isn't a Firestore
 * Timestamp or a Date is treated as no date at all, so a string goLiveDate is
 * dropped here exactly as the report drops it.
 * @param {*} value
 * @returns {Date|null}
 */
function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

/**
 * Count episodes with an upcoming publish date that aren't scheduled on every
 * platform.
 *
 * The two predicates below must stay in step with
 * PodcastPublishSchedulingReport's `loadRows` — a badge counting rows the
 * report then filters out reads as "1 to do" above an empty page.
 *
 * @param {Array} docs - Firestore query docs
 * @returns {number}
 */
function countNeedsScheduling(docs) {
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  let count = 0;

  for (const docSnap of docs) {
    const p = docSnap.data();
    if (p.type !== 'episode') continue;

    // Past go-live dates are the report's problem to chase down elsewhere; it
    // only lists upcoming episodes, so the badge must not count them.
    const goLiveDate = toDate(p.goLiveDate);
    if (!goLiveDate || goLiveDate.getTime() < startOfToday) continue;

    const ps = p.publishScheduling || {};
    const allScheduled = PLATFORMS.every((platform) => ps[platform]?.scheduled === true);
    if (!allScheduled) count += 1;
  }

  return count;
}

export const usePublishSchedulingStore = defineStore('publishScheduling', () => {
  const actionCount = ref(0);
  const initialized = ref(false);
  let unsubscribe = null;

  function init() {
    if (initialized.value) return;
    initialized.value = true;

    try {
      const q = query(collection(db, 'projects'), where('type', '==', 'episode'));
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          actionCount.value = countNeedsScheduling(snapshot.docs);
        },
        (error) => {
          logger.firestoreListenerError('[PublishSchedulingStore] Snapshot error', error);
        }
      );
    } catch (error) {
      logger.error('[PublishSchedulingStore] Failed to init', error);
    }
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    initialized.value = false;
    actionCount.value = 0;
  }

  return { actionCount, initialized, init, teardown };
});
